import { isErrorResponse } from '@standardnotes/responses';
import { WebSocketsServiceEvent } from './WebSocketsServiceEvent';
import { AbstractService } from '../Service/AbstractService';
import { StorageKey } from '../Storage/StorageKeys';
import { Result } from '@standardnotes/domain-core';
export class WebSocketsService extends AbstractService {
    constructor(storageService, webSocketUrl, webSocketApiService, internalEventBus) {
        super(internalEventBus);
        this.storageService = storageService;
        this.webSocketUrl = webSocketUrl;
        this.webSocketApiService = webSocketApiService;
        this.internalEventBus = internalEventBus;
        this.CLOSE_CONNECTION_CODE = 3123;
        this.HEARTBEAT_DELAY = 360000;
    }
    setWebSocketUrl(url) {
        this.webSocketUrl = url;
        this.storageService.setValue(StorageKey.WebSocketUrl, url);
    }
    loadWebSocketUrl() {
        const storedValue = this.storageService.getValue(StorageKey.WebSocketUrl);
        this.webSocketUrl =
            storedValue ||
                this.webSocketUrl ||
                window._websocket_url;
    }
    async startWebSocketConnection() {
        if (!this.webSocketUrl) {
            return Result.fail('WebSocket URL is not set');
        }
        const webSocketConectionToken = await this.createWebSocketConnectionToken();
        if (webSocketConectionToken === undefined) {
            return Result.fail('Failed to create WebSocket connection token');
        }
        try {
            this.webSocket = new WebSocket(`${this.webSocketUrl}?authToken=${webSocketConectionToken}`);
            this.webSocket.onmessage = this.onWebSocketMessage.bind(this);
            this.webSocket.onclose = this.onWebSocketClose.bind(this);
            this.webSocket.onopen = this.beginWebSocketHeartbeat.bind(this);
            return Result.ok();
        }
        catch (error) {
            return Result.fail(`Error starting WebSocket connection: ${error.message}`);
        }
    }
    isWebSocketConnectionOpen() {
        var _a;
        return ((_a = this.webSocket) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN;
    }
    closeWebSocketConnection() {
        var _a;
        (_a = this.webSocket) === null || _a === void 0 ? void 0 : _a.close(this.CLOSE_CONNECTION_CODE, 'Closing application');
    }
    beginWebSocketHeartbeat() {
        this.webSocketHeartbeatInterval = setInterval(this.websocketHeartbeat.bind(this), this.HEARTBEAT_DELAY);
    }
    websocketHeartbeat() {
        var _a;
        if (((_a = this.webSocket) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
            this.webSocket.send('ping');
        }
    }
    onWebSocketMessage(messageEvent) {
        const eventData = JSON.parse(messageEvent.data);
        switch (eventData.type) {
            case 'ITEMS_CHANGED_ON_SERVER':
                void this.notifyEvent(WebSocketsServiceEvent.ItemsChangedOnServer, eventData);
                break;
            case 'USER_ROLES_CHANGED':
                void this.notifyEvent(WebSocketsServiceEvent.UserRoleMessageReceived, eventData);
                break;
            case 'NOTIFICATION_ADDED_FOR_USER':
                void this.notifyEvent(WebSocketsServiceEvent.NotificationAddedForUser, eventData.payload);
                break;
            case 'MESSAGE_SENT_TO_USER':
                void this.notifyEvent(WebSocketsServiceEvent.MessageSentToUser, eventData.payload);
                break;
            case 'USER_INVITED_TO_SHARED_VAULT':
                void this.notifyEvent(WebSocketsServiceEvent.UserInvitedToSharedVault, eventData.payload);
                break;
            default:
                break;
        }
    }
    onWebSocketClose(event) {
        var _a;
        if (this.webSocketHeartbeatInterval) {
            clearInterval(this.webSocketHeartbeatInterval);
        }
        this.webSocketHeartbeatInterval = undefined;
        const closedByApplication = event.code === this.CLOSE_CONNECTION_CODE;
        if (closedByApplication) {
            this.webSocket = undefined;
            return;
        }
        if (((_a = this.webSocket) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.CLOSED) {
            void this.startWebSocketConnection();
        }
    }
    async createWebSocketConnectionToken() {
        try {
            const response = await this.webSocketApiService.createConnectionToken();
            if (isErrorResponse(response)) {
                console.error(response.data.error);
                return undefined;
            }
            return response.data.token;
        }
        catch (error) {
            console.error('Caught error:', error.message);
            return undefined;
        }
    }
    deinit() {
        super.deinit();
        this.storageService = undefined;
        this.webSocketApiService = undefined;
        this.closeWebSocketConnection();
    }
}
